import {
  useToggleIntegrationMutation,
  useUpdateIntegrationMutation,
  useUpdateStepMutation,
} from "features/integrationsBeta/integrationsBeta";
import { useUpdateConfigProfileMutation } from "features/integrationsBeta/configProfiles";
import { useUpdateConnectionMutation } from "features/integrationsBeta/connections";
import {
  useToggleScheduleMutation,
  useUpdateScheduleMutation,
} from "features/integrationsBeta/schedules";
import { useUpdateWebhookTriggerMutation } from "features/integrationsBeta/webhookTriggers";
import { formatApiError } from "utils/formatApiError";
import { usePendingChanges, type PendingChange } from "./PendingChangesContext";

export interface FlushFailure {
  entityLabel: string;
  message: string;
}

type Json = Record<string, unknown>;

/**
 * Applies staged changes: ONE mutation per (entityType, entityId) with the
 * merged payload; `is_active` of the integration and of schedules route
 * through their dedicated toggle endpoints (they cascade server-side).
 * Sequential awaits keep invalidation waves ordered; schedule toggles run
 * LAST so explicit per-schedule intent survives the integration cascade.
 * Succeeded records leave the store; failed ones stay staged.
 */
export function useFlushPendingChanges(integrationId: number) {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [toggleIntegration] = useToggleIntegrationMutation();
  const [updateProfile] = useUpdateConfigProfileMutation();
  const [updateConnection] = useUpdateConnectionMutation();
  const [updateStep] = useUpdateStepMutation();
  const [updateTrigger] = useUpdateWebhookTriggerMutation();
  const [updateSchedule] = useUpdateScheduleMutation();
  const [toggleSchedule] = useToggleScheduleMutation();
  const { removeKeys } = usePendingChanges();

  return async (changes: PendingChange[]): Promise<FlushFailure[]> => {
    interface Op {
      order: number;
      entityLabel: string;
      keys: string[];
      run: () => Promise<unknown>;
    }
    const ops: Op[] = [];

    const byEntity = new Map<string, PendingChange[]>();
    for (const change of changes) {
      const key = `${change.entityType}:${change.entityId}`;
      const list = byEntity.get(key) ?? [];
      list.push(change);
      byEntity.set(key, list);
    }

    const merged = (rows: PendingChange[]) =>
      Object.fromEntries(rows.map((r) => [r.field, r.newValue]));

    byEntity.forEach((recs) => {
      const { entityType, entityId, entityLabel } = recs[0];
      switch (entityType) {
        case "integration": {
          const toggle = recs.find((r) => r.field === "is_active");
          const rest = recs.filter((r) => r.field !== "is_active");
          if (rest.length) {
            ops.push({
              order: 0,
              entityLabel: "Integration",
              keys: rest.map((r) => r.key),
              run: () =>
                updateIntegration({ id: entityId, ...merged(rest) }).unwrap(),
            });
          }
          if (toggle) {
            // Dedicated endpoint; cascades to all schedules server-side.
            ops.push({
              order: 1,
              entityLabel: "Integration status",
              keys: [toggle.key],
              run: () =>
                toggleIntegration({
                  id: entityId,
                  is_active: toggle.newValue as boolean,
                }).unwrap(),
            });
          }
          break;
        }
        case "configProfile": {
          const config = recs.find((r) => r.field === "config");
          if (config) {
            ops.push({
              order: 2,
              entityLabel,
              keys: recs.map((r) => r.key),
              run: () =>
                updateProfile({
                  id: entityId,
                  config: config.newValue as Json,
                }).unwrap(),
            });
          }
          break;
        }
        case "connection":
          ops.push({
            order: 3,
            entityLabel,
            keys: recs.map((r) => r.key),
            run: () =>
              updateConnection({ id: entityId, ...merged(recs) }).unwrap(),
          });
          break;
        case "step":
          ops.push({
            order: 4,
            entityLabel,
            keys: recs.map((r) => r.key),
            run: () =>
              updateStep({
                integrationId,
                id: entityId,
                ...merged(recs),
              }).unwrap(),
          });
          break;
        case "webhookTrigger":
          ops.push({
            order: 5,
            entityLabel,
            keys: recs.map((r) => r.key),
            run: () =>
              updateTrigger({
                id: entityId,
                integration: integrationId,
                ...merged(recs),
              }).unwrap(),
          });
          break;
        case "schedule": {
          const toggle = recs.find((r) => r.field === "is_active");
          const rest = recs.filter((r) => r.field !== "is_active");
          if (rest.length) {
            ops.push({
              order: 6,
              entityLabel,
              keys: rest.map((r) => r.key),
              run: () =>
                updateSchedule({
                  id: entityId,
                  integration: integrationId,
                  ...merged(rest),
                }).unwrap(),
            });
          }
          if (toggle) {
            ops.push({
              order: 7,
              entityLabel: `${entityLabel} status`,
              keys: [toggle.key],
              run: () =>
                toggleSchedule({
                  id: entityId,
                  integration: integrationId,
                  is_active: toggle.newValue as boolean,
                }).unwrap(),
            });
          }
          break;
        }
      }
    });

    ops.sort((a, b) => a.order - b.order);

    const failures: FlushFailure[] = [];
    const succeeded: string[] = [];
    for (const op of ops) {
      try {
        await op.run();
        succeeded.push(...op.keys);
      } catch (err) {
        failures.push({
          entityLabel: op.entityLabel,
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Update failed.",
          ),
        });
      }
    }
    if (succeeded.length) removeKeys(succeeded);
    return failures;
  };
}
